import { ObjectCategory } from "@common/constants";
import { type Hitbox, HitboxType } from "@common/utils/hitbox";
import { LayerContainer } from "@common/utils/layer";
import { BlurFilter, Container, Filter, GlProgram, NineSliceSprite, Rectangle, RendererType, RenderTexture, Sprite, Texture, defaultFilterVert } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { CameraManager } from "./cameraManager";
import { Lighting } from "./lighting";
import { isShadowLevel, type ShadowLevel } from "./lightingLogic";
import { ANIMAL_HEIGHTS, buildingHeight, casterHeight, CONTACT, contactBand, DISC_EDGES, discEdge, HEIGHTS, SHADOW_LOOKS, SHADOW_ZINDEX, shadowLength, SILHOUETTE_FRAGMENT, sweepStops } from "./shadowLogic";

/*
    The shadows (0.98.6, owner decision, 1 November 2026: "Shadows high and low or OFF": Settings → Graphics,
    `cv_shadows`): everything standing on the ground floor under the sky throws a shadow of its own picture along the
    sun (managers/lighting.ts says which way, how long, how dark and how soft; by day short and down to the right, at
    dusk long and plum toward the upper right, on snow blue, under a thunderstorm faint and grey, hard and black while
    the lightning flashes): the trees' crowns and trunks, the rocks, the bushes, the crates and barrels, the cars, the
    walls, the players and their hands, the animals, the loot, and (High) the buildings' roofs.

    They're drawn as one picture each frame: every shadow a copy of its thing's picture put down along the sun (High:
    swept from the thing's foot to its shadow's end, several copies joined, so a tall thing's shadow is the shape its
    height throws and not a disc beside it; Low: once, where a hard sun throws it) into a small texture (half the
    screen's pixels at High, less at Low), as a white silhouette (a filter keeps the picture's shape and drops its
    colours; on WebGPU, where a GLSL filter can't run, they're drawn black instead), blurred at High by the sun's
    softness; then every building's roof is erased from it where it lies, so no sun falls indoors (the furniture and
    whoever is inside have no cast shadow, as under a roof they wouldn't); and the texture is stretched over the
    screen among the ground floor's things, over its floors, decals and residues and under everything standing, in
    the sun's colour and darkness. Overlapping shadows don't darken each other (it's one picture): a wood's shade is
    even. Nothing of it on the Canvas 2D fallback (software rendering).

    The players' small contact shadow (objects/playerGear.ts) stays at Low and High and goes at Off, as the jump-in's
    shadows under the planes and the chutes don't (they're the jump's markers: managers/jumpIn.ts).

    0.98.7 (the owner: "Contact darkening"): a second picture each frame, the contact shade: a soft dark band round
    the foot of everything standing (a wall's both sides, a crate's, a tree's trunk's, a car's, a building's solid
    parts), wider and darker as the thing is taller (shadowLogic.ts `contactBand`), laid under the shadows among the
    ground floor's things, so a thing stands on the ground rather than floats on it. Low and High alike.
*/

/** the contact square's picture is this many pixels across, its soft border this many of them (the nine-slice's cut) */
const RECT_SIZE = 64;
const RECT_BORDER = 20;
/** the contact discs' pictures are this many pixels across */
const DISC_SIZE = 128;

/** how far past the screen a thing may stand and still throw a shadow onto it (game units) */
const MARGIN = 40;
/** a building's roof may be this big (game units from its middle): its shadow is looked at from further off */
const BUILDING_MARGIN = 220;

class ShadowLookClass {
    private _level: ShadowLevel = "high";
    private _listening = false;
    /** the shadows as the settings have them */
    get level(): ShadowLevel { return this._level; }
    /** the players' contact shadows show (Low and High) */
    get contact(): boolean { return this._level !== "off"; }

    private _target?: RenderTexture;
    private readonly _scene = new Container();
    private readonly _casters = new Container();
    private readonly _erasers = new Container();
    private _composite?: Sprite;
    private readonly _pool: Sprite[] = [];
    private _used = 0;
    private readonly _erasePool: Sprite[] = [];
    private _erased = 0;
    private _silhouette?: Filter;
    private _blur?: BlurFilter;
    private readonly _area = new Rectangle();
    private _filtersOn?: boolean;
    private _filterKey = "";
    /** the contact shade (0.98.7): its picture, its scene, its blots */
    private _contactTarget?: RenderTexture;
    private readonly _contactScene = new Container();
    private _contactComposite?: Sprite;
    private readonly _rectPool: NineSliceSprite[] = [];
    private _rectsUsed = 0;
    private readonly _discPool: Sprite[] = [];
    private _discsUsed = 0;
    private _contactDrawn = 0;
    /** the contact blots drawn this frame (tests and the lab) */
    get contactDrawn(): number { return this._contactDrawn; }
    /** the shadows drawn this frame (tests and the lab) */
    private _drawn = 0;
    get drawn(): number { return this._drawn; }

    constructor() {
        this._scene.addChild(this._casters, this._erasers);
        this._scene.eventMode = "none";
    }

    /** the setting, read as the match begins and whenever it's changed (it applies at once, mid-match too) */
    private _readLevel(): void {
        if (!this._listening) {
            this._listening = true;
            GameConsole.variables.addChangeListener("cv_shadows", () => this._readLevel());
        }
        const value = GameConsole.getBuiltInCVar("cv_shadows");
        this._level = isShadowLevel(value) ? value : "high";
    }

    /** the map came: the shadows read their setting (nothing of a match's is kept between matches) */
    begin(): void {
        this._readLevel();
        this._drawn = 0;
    }

    /** leaving a match */
    reset(): void {
        this._drawn = 0;
        if (this._composite) this._composite.visible = false;
        if (this._contactComposite) this._contactComposite.visible = false;
    }

    /** whether anything is drawn: the setting on, the match on, a renderer that can draw into a texture with blends */
    get active(): boolean {
        return this._level !== "off" && Game.gameStarted && Game.pixi.renderer.type !== RendererType.CANVAS;
    }

    private _sprite(): Sprite {
        let sprite = this._pool[this._used];
        if (!sprite) {
            sprite = new Sprite();
            sprite.eventMode = "none";
            this._pool.push(sprite);
            this._casters.addChild(sprite);
        }
        this._used++;
        sprite.visible = true;
        return sprite;
    }

    private _eraser(): Sprite {
        let sprite = this._erasePool[this._erased];
        if (!sprite) {
            sprite = new Sprite();
            sprite.eventMode = "none";
            sprite.blendMode = "erase";
            this._erasePool.push(sprite);
            this._erasers.addChild(sprite);
        }
        this._erased++;
        sprite.visible = true;
        return sprite;
    }

    /**
     * a picture's place in the world (pixels): its parent's position, turn and scale, then its own. The shadow's copies
     * are put down from there along the sun (`dirX`, `dirY`), `lengthPx` long, at each of `stops` (0 its foot, 1 its end)
     */
    private _cast(parent: Container, image: Sprite, lengthPx: number, sweep: boolean, dirX: number, dirY: number, black: boolean, view: View): void {
        const px = parent.position.x, py = parent.position.y;
        const prot = parent.rotation, psx = parent.scale.x, psy = parent.scale.y;
        const cos = Math.cos(prot), sin = Math.sin(prot);
        const ix = image.position.x * psx, iy = image.position.y * psy;
        const wx = px + ix * cos - iy * sin, wy = py + ix * sin + iy * cos;
        const sx = psx * image.scale.x, sy = psy * image.scale.y;
        const texture = image.texture;
        const size = Math.max(texture.orig.width * Math.abs(sx), texture.orig.height * Math.abs(sy));
        const reach = size + lengthPx;
        if (wx + reach < view.left || wx - reach > view.right || wy + reach < view.top || wy - reach > view.bottom) return;
        const rot = prot + image.rotation;
        for (const t of sweepStops(lengthPx, size, sweep)) {
            const sprite = this._sprite();
            if (sprite.texture !== texture) sprite.texture = texture;
            sprite.anchor.copyFrom(image.anchor);
            sprite.position.set(wx + dirX * t * lengthPx, wy + dirY * t * lengthPx);
            sprite.rotation = rot;
            sprite.scale.set(sx, sy);
            sprite.tint = black ? 0x000000 : 0xffffff;
        }
        this._drawn++;
    }

    /**
     * a roof erased where it lies: no sun under it. Its picture's whole frame, not its shape: a skylight's pane is a
     * hole in the picture, and through it the roof's own shadow fell on the floor
     */
    private _erase(parent: Container, image: Sprite, view: View): void {
        const px = parent.position.x, py = parent.position.y;
        const prot = parent.rotation, psx = parent.scale.x, psy = parent.scale.y;
        const cos = Math.cos(prot), sin = Math.sin(prot);
        const ix = image.position.x * psx, iy = image.position.y * psy;
        const wx = px + ix * cos - iy * sin, wy = py + ix * sin + iy * cos;
        const sx = psx * image.scale.x, sy = psy * image.scale.y;
        const { width, height } = image.texture.orig;
        const size = Math.max(width * Math.abs(sx), height * Math.abs(sy));
        if (wx + size < view.left || wx - size > view.right || wy + size < view.top || wy - size > view.bottom) return;
        const sprite = this._eraser();
        if (sprite.texture !== Texture.WHITE) sprite.texture = Texture.WHITE;
        sprite.anchor.copyFrom(image.anchor);
        sprite.position.set(wx, wy);
        sprite.rotation = prot + image.rotation;
        // (Texture.WHITE is a pixel: scaled to the picture's frame)
        sprite.scale.set(sx * width, sy * height);
    }

    private _rectBlot(): NineSliceSprite {
        let blot = this._rectPool[this._rectsUsed];
        if (!blot) {
            blot = new NineSliceSprite({ texture: contactTextures().rect, leftWidth: RECT_BORDER, topHeight: RECT_BORDER, rightWidth: RECT_BORDER, bottomHeight: RECT_BORDER });
            blot.eventMode = "none";
            this._rectPool.push(blot);
            this._contactScene.addChild(blot);
        }
        this._rectsUsed++;
        blot.visible = true;
        return blot;
    }

    private _discBlot(): Sprite {
        let blot = this._discPool[this._discsUsed];
        if (!blot) {
            blot = new Sprite(contactTextures().discs[0]);
            blot.anchor.set(0.5);
            blot.eventMode = "none";
            this._discPool.push(blot);
            this._contactScene.addChild(blot);
        }
        this._discsUsed++;
        blot.visible = true;
        return blot;
    }

    /** a thing's foot shaded: its hitbox (game units) with a band round it, by its height */
    private _contact(hitbox: Hitbox, height: number, view: View): void {
        const { band, alpha } = contactBand(height);
        if (alpha <= 0.01) return;
        switch (hitbox.type) {
            case HitboxType.Rect: {
                const minX = (hitbox.min.x - band) * PIXI_SCALE, minY = (hitbox.min.y - band) * PIXI_SCALE;
                const maxX = (hitbox.max.x + band) * PIXI_SCALE, maxY = (hitbox.max.y + band) * PIXI_SCALE;
                if (maxX < view.left || minX > view.right || maxY < view.top || minY > view.bottom) return;
                const blot = this._rectBlot();
                // (0.98.7's audit: a nine-slice's border is where it cuts its picture too, in the picture's pixels: it
                // stays the picture's soft border, and the blot is scaled so that border is the band's width; its
                // size, in its own pixels, the rest. Never under the two borders: the band is on both sides of it)
                const k = (band * PIXI_SCALE) / RECT_BORDER;
                blot.position.set(minX, minY);
                blot.scale.set(k);
                blot.width = (maxX - minX) / k;
                blot.height = (maxY - minY) / k;
                blot.alpha = alpha;
                this._contactDrawn++;
                break;
            }
            case HitboxType.Circle: {
                const r = (hitbox.radius + band) * PIXI_SCALE;
                const x = hitbox.position.x * PIXI_SCALE, y = hitbox.position.y * PIXI_SCALE;
                if (x + r < view.left || x - r > view.right || y + r < view.top || y - r > view.bottom) return;
                const blot = this._discBlot();
                // (the disc its radius and its band across, its soft edge the band: the disc of the nearest edge,
                // 0.98.7's audit; full at the thing's foot, nothing a band out)
                const texture = contactTextures().discs[discEdge(hitbox.radius, band)];
                if (blot.texture !== texture) blot.texture = texture;
                blot.position.set(x, y);
                blot.width = blot.height = r * 2;
                blot.alpha = alpha;
                this._contactDrawn++;
                break;
            }
            case HitboxType.Group:
                for (const part of hitbox.hitboxes) this._contact(part, height, view);
                break;
            default:
                break;
        }
    }

    /** the pools' sprites past this frame's, up to last frame's, hidden (0.98.8) */
    private _hideUnused(before: { readonly used: number, readonly erased: number, readonly rects: number, readonly discs: number }): void {
        for (let i = this._used; i < before.used; i++) this._pool[i].visible = false;
        for (let i = this._erased; i < before.erased; i++) this._erasePool[i].visible = false;
        for (let i = this._rectsUsed; i < before.rects; i++) this._rectPool[i].visible = false;
        for (let i = this._discsUsed; i < before.discs; i++) this._discPool[i].visible = false;
    }

    /** every frame, after the camera and the lighting (the sun), before the frame is drawn */
    update(): void {
        // (0.98.8: the copies and blots drawn last frame and not this frame are hidden once this frame's are placed, the
        // rest left shown: hidden and shown again every frame, they had Pixi build their pictures' draw lists again)
        const before = { used: this._used, erased: this._erased, rects: this._rectsUsed, discs: this._discsUsed };
        this._used = 0;
        this._erased = 0;
        this._rectsUsed = 0;
        this._discsUsed = 0;
        this._drawn = 0;
        this._contactDrawn = 0;
        if (!this.active) {
            this._hideUnused(before);
            if (this._composite) this._composite.visible = false;
            if (this._contactComposite) this._contactComposite.visible = false;
            return;
        }
        const ground = CameraManager.layerContainers[LayerContainer.Ground];
        // (down in a bunker the ground floor isn't shown: nothing to draw)
        if (!ground.visible || ground.alpha <= 0.01) {
            this._hideUnused(before);
            if (this._composite) this._composite.visible = false;
            if (this._contactComposite) this._contactComposite.visible = false;
            return;
        }
        const look = SHADOW_LOOKS[this._level === "high" ? "high" : "low"];
        const sun = Lighting.sun;
        const renderer = Game.pixi.renderer;
        const screen = Game.pixi.screen;
        const width = Math.max(1, Math.ceil(screen.width * look.scale)), height = Math.max(1, Math.ceil(screen.height * look.scale));
        if (!this._target) {
            this._target = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
        } else if (this._target.width !== width || this._target.height !== height) {
            this._target.resize(width, height);
        }
        if (!this._composite || this._composite.destroyed) {
            this._composite = new Sprite(this._target);
            this._composite.zIndex = SHADOW_ZINDEX;
            this._composite.eventMode = "none";
        }
        // (a GLSL filter runs on WebGL only: elsewhere the shadows are drawn black, and the sun's colour isn't theirs)
        const filtersOn = renderer.type === RendererType.WEBGL;
        if (this._filtersOn !== filtersOn) {
            this._filtersOn = filtersOn;
            if (filtersOn) {
                this._silhouette ??= new Filter({ glProgram: GlProgram.from({ vertex: defaultFilterVert, fragment: SILHOUETTE_FRAGMENT, name: "shadow-silhouette" }), resources: {} });
            }
        }
        this._blur ??= new BlurFilter({ strength: 4, quality: 2, kernelSize: 5 });
        const soft = look.blur * (0.2 + 0.8 * sun.soft);
        const blurred = soft >= 0.5;
        if (blurred) this._blur.strength = soft;
        // (the filters set again only as they change: a new list each frame would have Pixi rebuild the scene each frame)
        const key = `${filtersOn && this._silhouette ? "s" : ""}${blurred ? "b" : ""}`;
        if (key !== this._filterKey) {
            this._filterKey = key;
            const filters: Filter[] = [];
            if (filtersOn && this._silhouette) filters.push(this._silhouette);
            if (blurred) filters.push(this._blur);
            this._casters.filters = filters;
        }

        // the camera: a point of the world (pixels) is on screen at offset + scale × it; on the shadows' picture, that times its scale
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const k = look.scale;
        this._scene.scale.set(scale * k, scale * k);
        this._scene.position.set(offset.x * k, offset.y * k);
        const left = -offset.x / scale, top = -offset.y / scale;
        const w = screen.width / scale, h = screen.height / scale;
        const view: View = { left: left - MARGIN * PIXI_SCALE, top: top - MARGIN * PIXI_SCALE, right: left + w + MARGIN * PIXI_SCALE, bottom: top + h + MARGIN * PIXI_SCALE };
        // (the filters' frame: the screen, so Pixi needn't measure every shadow each frame)
        this._area.x = left; this._area.y = top; this._area.width = w; this._area.height = h;
        this._casters.filterArea = this._area;

        const dirX = Math.cos(sun.angle), dirY = Math.sin(sun.angle);
        const black = !filtersOn;
        const lengthOf = (height: number, building = false): number => shadowLength(height, sun.stretch, building) * PIXI_SCALE;

        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            if (obstacle.destroyed || obstacle.dead || obstacle.layerContainer !== ground || !obstacle.container.visible) continue;
            const height = casterHeight(obstacle.definition);
            if (height <= 0) continue;
            // (0.98.7: its foot shaded, by the hitbox it stands on)
            if (obstacle.hitbox && !obstacle.definition.isDoor && height >= CONTACT.least) this._contact(obstacle.hitbox, height, view);
            const length = lengthOf(height);
            if (obstacle.image.visible && obstacle.image.texture.orig.width > 1) this._cast(obstacle.container, obstacle.image, length, look.sweep, dirX, dirY, black, view);
            const crown = obstacle.leavesSprite;
            if (crown?.visible) this._cast(obstacle.container, crown, length, look.sweep, dirX, dirY, black, view);
        }

        // the buildings: their roofs throw shadows (High), and under every roof the sun is erased (Low too)
        const buildingView: View = { left: view.left - BUILDING_MARGIN * PIXI_SCALE, top: view.top - BUILDING_MARGIN * PIXI_SCALE, right: view.right + BUILDING_MARGIN * PIXI_SCALE, bottom: view.bottom + BUILDING_MARGIN * PIXI_SCALE };
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            // (a bunker's rooms are under the ground: nothing of them in the sun)
            if (building.destroyed || building.dead || building.layer < 0) continue;
            const definition = building.definition;
            // (0.98.7: a building's solid parts' feet shaded, as walls')
            if (building.hitbox && building.layerContainer === ground) this._contact(building.hitbox, HEIGHTS.wall, view);
            // (a roof's deck is drawn as its floor, on the floor above: the roof is what it is)
            const deck = definition.openAir === true;
            const parent = deck ? building.container : building.ceilingContainer;
            const length = lengthOf(buildingHeight(building.layer), true);
            for (const image of building.images.values()) {
                if (image.isCeiling === deck || !image.sprite.visible) continue;
                if (look.roofs) this._cast(parent, image.sprite, length, look.sweep, dirX, dirY, black, buildingView);
                this._erase(parent, image.sprite, buildingView);
            }
        }

        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            if (player.destroyed || player.dead || player.air || player.layerContainer !== ground || !player.container.visible) continue;
            const length = lengthOf((player.downed ? HEIGHTS.downed : HEIGHTS.player) * player.sizeMod);
            const { body, leftFist, rightFist, weapon, altWeapon } = player.images;
            if (body.visible) this._cast(player.container, body, length, look.sweep, dirX, dirY, black, view);
            if (leftFist.visible) this._cast(player.container, leftFist, length, false, dirX, dirY, black, view);
            if (rightFist.visible) this._cast(player.container, rightFist, length, false, dirX, dirY, black, view);
            // (the gun in hand, held at the body's height)
            if (weapon.visible && weapon.texture.orig.width > 1) this._cast(player.container, weapon, length, false, dirX, dirY, black, view);
            if (altWeapon.visible && altWeapon.texture.orig.width > 1) this._cast(player.container, altWeapon, length, false, dirX, dirY, black, view);
        }

        for (const animal of Game.objects.getCategory(ObjectCategory.Animal)) {
            // (a flier in the air throws its own: objects/animal.ts)
            if (animal.destroyed || animal.dead || animal.height > 0 || animal.layerContainer !== ground || !animal.container.visible) continue;
            this._cast(animal.container, animal.body, lengthOf(ANIMAL_HEIGHTS[animal.definition.kind]), look.sweep, dirX, dirY, black, view);
        }

        for (const loot of Game.objects.getCategory(ObjectCategory.Loot)) {
            if (loot.destroyed || loot.layerContainer !== ground || !loot.container.visible) continue;
            const background = loot.images.background;
            if (background.visible && background.texture.orig.width > 1) this._cast(loot.container, background, lengthOf(HEIGHTS.loot), false, dirX, dirY, black, view);
        }

        this._hideUnused(before);
        renderer.render({ container: this._scene, target: this._target, clear: true, clearColor: [0, 0, 0, 0] });

        // stretched over the screen among the ground floor's things, in the sun's colour and darkness
        const composite = this._composite;
        if (composite.parent !== ground) ground.addChild(composite);
        composite.visible = true;
        composite.position.set(left, top);
        composite.scale.set(w / width, h / height);
        composite.tint = black ? 0xffffff : sun.tint;
        composite.alpha = sun.alpha;

        // the contact shade (0.98.7): its picture, black, under the shadows
        if (!this._contactTarget) {
            this._contactTarget = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
        } else if (this._contactTarget.width !== width || this._contactTarget.height !== height) {
            this._contactTarget.resize(width, height);
        }
        if (!this._contactComposite || this._contactComposite.destroyed) {
            this._contactComposite = new Sprite(this._contactTarget);
            this._contactComposite.zIndex = SHADOW_ZINDEX - 0.01;
            this._contactComposite.tint = 0x000000;
            this._contactComposite.eventMode = "none";
        }
        this._contactScene.scale.set(scale * k, scale * k);
        this._contactScene.position.set(offset.x * k, offset.y * k);
        renderer.render({ container: this._contactScene, target: this._contactTarget, clear: true, clearColor: [0, 0, 0, 0] });
        const contact = this._contactComposite;
        if (contact.parent !== ground) ground.addChild(contact);
        contact.visible = this._contactDrawn > 0;
        contact.position.set(left, top);
        contact.scale.set(w / width, h / height);
        contact.alpha = CONTACT.alpha;
    }
}

interface View { readonly left: number, readonly top: number, readonly right: number, readonly bottom: number }

/** the blots' pictures (white, their alpha the shade): a soft-edged square (its border the band) and the soft discs, one per edge */
let contactTextureSet: { rect: Texture, discs: Texture[] } | undefined;
function contactTextures(): { rect: Texture, discs: Texture[] } {
    if (contactTextureSet && !contactTextureSet.rect.destroyed) return contactTextureSet;
    const make = (size: number, alpha: (x: number, y: number) => number): Texture => {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (ctx) {
            const image = ctx.createImageData(size, size);
            for (let y = 0; y < size; y++) {
                for (let x = 0; x < size; x++) {
                    const i = (y * size + x) * 4;
                    image.data[i] = image.data[i + 1] = image.data[i + 2] = 255;
                    image.data[i + 3] = Math.round(255 * Math.max(0, Math.min(1, alpha(x + 0.5, y + 0.5))));
                }
            }
            ctx.putImageData(image, 0, 0);
        }
        return Texture.from(canvas);
    };
    const smooth = (t: number): number => {
        const k = Math.max(0, Math.min(1, t));
        return k * k * (3 - 2 * k);
    };
    // the square: 64 across, its border of 20 soft (full at the inner edge, nothing at the outer), the middle full
    const rect = make(RECT_SIZE, (x, y) => {
        const dx = Math.min(x, RECT_SIZE - x), dy = Math.min(y, RECT_SIZE - y);
        // (the corner rounded: the distance to the inner square's corner)
        const inner = RECT_BORDER;
        const ox = Math.max(0, inner - dx), oy = Math.max(0, inner - dy);
        const d = Math.hypot(ox, oy);
        return smooth(1 - d / inner);
    });
    // the discs: full to the edge's start, nothing at the rim, one for each of DISC_EDGES
    const half = DISC_SIZE / 2;
    const discs = DISC_EDGES.map(edge => make(DISC_SIZE, (x, y) => {
        const r = Math.hypot(x - half, y - half) / half;
        return 1 - smooth((r - (1 - edge)) / edge);
    }));
    return contactTextureSet = { rect, discs };
}

export const ShadowLook = new ShadowLookClass();
